import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../../home/components/Footer';

export default function ElectricalPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openScheduler = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) widget.click();
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncodedData = new URLSearchParams();
    
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      await fetch('https://readdy.ai/api/form/d6937mlt1k61917cm2j0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });
      setSubmitSuccess(true);
      form.reset();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="cursor-pointer">
              <div className="text-2xl font-bold leading-tight text-[#2C2C2C]">RW Construction</div>
              <div className="text-sm leading-tight text-gray-600">Las Vegas LLC</div>
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Home</Link>
              <Link to="/services/pool-repair" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Pool Repair</Link>
              <Link to="/services/synthetic-grass" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Synthetic Grass</Link>
              <Link to="/services/pavers" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Pavers</Link>
              <Link to="/services/electrical" className="text-sm font-medium text-[#FF6B35] transition-colors cursor-pointer">Electrical</Link>
              <button onClick={openScheduler} className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#E55A2B] transition-colors whitespace-nowrap cursor-pointer">
                Get Free Estimate
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6eb7aa61c32189a15429af2b348f136f.jpg"
            alt="Professional Electrical Services Las Vegas"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-[#FF6B35] px-4 py-2 rounded-full mb-6">
            <i className="ri-flashlight-line text-white"></i>
            <span className="text-white text-sm font-semibold">Licensed Electrical Contractors</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Professional Electrical Services in Las Vegas
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-8 max-w-3xl mx-auto">
            From panel upgrades to solar panel repair, our licensed electricians deliver safe, code-compliant electrical solutions for homes and businesses throughout the Las Vegas Valley. 20+ years of trusted service.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-[#FF6B35] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#E55A2B] transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Schedule Electrical Inspection
            </button>
            <a
              href="tel:7027606213"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-760-6213
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Our Expertise</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Comprehensive Electrical Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We provide complete electrical solutions for residential and commercial properties, ensuring safety and efficiency.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-dashboard-3-line', title: 'Electrical Panel Upgrades', desc: 'Upgrade your outdated electrical panel to handle modern power demands safely. We install 100, 200, and 400 amp panels.', features: ['Panel replacement', 'Capacity upgrades', 'Code compliance', 'Safety inspections'] },
              { icon: 'ri-plug-line', title: 'Wiring & Rewiring', desc: 'Complete home rewiring services to replace old, dangerous wiring with modern, safe electrical systems.', features: ['Whole-house rewiring', 'Aluminum to copper', 'Knob & tube replacement', 'Code updates'] },
              { icon: 'ri-lightbulb-line', title: 'Lighting Installation', desc: 'Transform your space with professional lighting design and installation, from recessed lights to chandeliers.', features: ['Recessed lighting', 'LED upgrades', 'Landscape lighting', 'Smart lighting'] },
              { icon: 'ri-plug-2-line', title: 'Outlet & Switch Installation', desc: 'Add new outlets, upgrade to GFCI/AFCI protection, and install smart switches throughout your property.', features: ['GFCI outlets', 'USB outlets', 'Smart switches', 'Dedicated circuits'] },
              { icon: 'ri-sun-line', title: 'Solar Panel Repair', desc: 'Expert repair and maintenance for solar panel systems to maximize your energy production and savings.', features: ['Panel diagnostics', 'Inverter repair', 'Wiring fixes', 'Performance optimization'] },
              { icon: 'ri-car-line', title: 'EV Charger Installation', desc: 'Install Level 2 electric vehicle chargers at your home or business for fast, convenient charging.', features: ['Level 2 chargers', 'Tesla Wall Connector', 'Commercial stations', 'Load management'] },
              { icon: 'ri-home-gear-line', title: 'Whole-House Generators', desc: 'Never lose power again with automatic standby generators that keep your home running during outages.', features: ['Generac installation', 'Transfer switches', 'Maintenance plans', 'Load calculations'] },
              { icon: 'ri-building-line', title: 'Commercial Electrical', desc: 'Complete electrical services for businesses including tenant improvements, lighting retrofits, and power distribution.', features: ['Tenant buildouts', 'Lighting retrofits', 'Power distribution', 'Emergency systems'] },
              { icon: 'ri-tools-line', title: 'Electrical Repairs', desc: 'Fast, reliable repairs for all electrical issues including flickering lights, tripped breakers, and faulty outlets.', features: ['Troubleshooting', 'Circuit repairs', 'Breaker replacement', 'Emergency service'] },
            ].map((service, index) => (
              <div key={index} className="bg-[#F8F6F3] rounded-2xl p-8 hover:shadow-xl transition-all group">
                <div className="w-16 h-16 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.desc}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#FF6B35]"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety First Section */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20residential%20electrical%20panel%20with%20organized%20wiring%20and%20circuit%20breakers%2C%20professional%20installation%20in%20Las%20Vegas%20home%2C%20clean%20white%20wall%20background%2C%20safety%20labels%20visible%2C%20high%20quality%20professional%20photography&width=600&height=700&seq=electrical-panel-001&orientation=portrait"
                alt="Professional Electrical Panel Installation"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Safety First</span>
              <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-6">Why Electrical Safety Matters</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Electrical issues are a leading cause of home fires. Our licensed electricians ensure every installation and repair meets or exceeds NEC code requirements, protecting your family and property.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-shield-check-line', title: 'Code Compliant', desc: 'All work meets current NEC and local codes' },
                  { icon: 'ri-file-list-3-line', title: 'Permit & Inspection', desc: 'We pull permits and schedule inspections' },
                  { icon: 'ri-award-line', title: 'Licensed & Insured', desc: 'Fully licensed Nevada electrical contractors' },
                  { icon: 'ri-time-line', title: '24/7 Emergency', desc: 'Available for urgent electrical emergencies' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 bg-white rounded-xl p-4">
                    <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#FF6B35] text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2C2C2C]">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">How It Works</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Our Electrical Service Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We follow a systematic approach to ensure safe, efficient, and code-compliant electrical work.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Free Consultation', desc: 'We assess your electrical needs and provide expert recommendations for your project.' },
              { step: '02', title: 'Detailed Estimate', desc: 'Receive a transparent quote with itemized pricing and project timeline.' },
              { step: '03', title: 'Professional Installation', desc: 'Our licensed electricians complete the work to the highest safety standards.' },
              { step: '04', title: 'Inspection & Testing', desc: 'We test all work and schedule city inspection for permit sign-off.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Our Work</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Recent Electrical Projects</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">See examples of our professional electrical installations throughout Las Vegas.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { img: 'https://readdy.ai/api/search-image?query=Modern%20200%20amp%20electrical%20panel%20upgrade%20installation%20in%20Las%20Vegas%20home%2C%20organized%20circuit%20breakers%20with%20clear%20labels%2C%20professional%20wiring%2C%20clean%20white%20background%20wall%2C%20high%20quality%20photography&width=600&height=450&seq=electrical-work-001&orientation=landscape', title: '200 Amp Panel Upgrade', location: 'Henderson, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Tesla%20Wall%20Connector%20EV%20charger%20installation%20in%20residential%20garage%20Las%20Vegas%2C%20sleek%20white%20charger%20mounted%20on%20wall%2C%20professional%20electrical%20work%2C%20modern%20clean%20garage%20setting%2C%20high%20quality%20photography&width=600&height=450&seq=electrical-work-002&orientation=landscape', title: 'EV Charger Installation', location: 'Summerlin, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Modern%20recessed%20LED%20lighting%20installation%20in%20luxury%20Las%20Vegas%20home%20living%20room%2C%20warm%20ambient%20lighting%2C%20contemporary%20interior%20design%2C%20professional%20lighting%20layout%2C%20high%20quality%20interior%20photography&width=600&height=450&seq=electrical-work-003&orientation=landscape', title: 'Whole-House LED Retrofit', location: 'Las Vegas, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Generac%20whole%20house%20standby%20generator%20installation%20outside%20Las%20Vegas%20home%2C%20professional%20electrical%20connection%2C%20clean%20concrete%20pad%2C%20desert%20landscaping%20background%2C%20high%20quality%20photography&width=600&height=450&seq=electrical-work-004&orientation=landscape', title: 'Standby Generator Install', location: 'North Las Vegas, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Solar%20panel%20system%20on%20residential%20roof%20Las%20Vegas%20home%20with%20clear%20blue%20sky%2C%20professional%20installation%20with%20clean%20wiring%2C%20desert%20neighborhood%20background%2C%20high%20quality%20aerial%20photography&width=600&height=450&seq=electrical-work-005&orientation=landscape', title: 'Solar Panel Repair', location: 'Green Valley, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Commercial%20electrical%20installation%20in%20Las%20Vegas%20retail%20store%2C%20modern%20LED%20track%20lighting%2C%20professional%20electrical%20panel%2C%20clean%20organized%20wiring%2C%20high%20quality%20commercial%20photography&width=600&height=450&seq=electrical-work-006&orientation=landscape', title: 'Commercial Buildout', location: 'Las Vegas, NV' },
            ].map((project, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="rounded-2xl overflow-hidden mb-4 shadow-lg">
                  <img
                    src={project.img}
                    alt={project.title}
                    className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-lg font-bold text-[#2C2C2C]">{project.title}</h3>
                <p className="text-gray-500 text-sm flex items-center gap-1">
                  <i className="ri-map-pin-line text-[#FF6B35]"></i>
                  {project.location}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#2C2C2C]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Why Choose Us</span>
              <h2 className="text-4xl font-bold text-white mt-3 mb-6">Las Vegas Electrical Experts</h2>
              <p className="text-white/80 text-base leading-relaxed mb-8">
                With over 20 years of experience serving the Las Vegas Valley, we understand the unique electrical challenges of desert living. From extreme heat affecting outdoor equipment to the growing demand for EV chargers and solar systems, we have the expertise to handle it all.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: 'ri-medal-line', title: '20+ Years', desc: 'Experience' },
                  { icon: 'ri-shield-star-line', title: 'Licensed', desc: '& Insured' },
                  { icon: 'ri-home-4-line', title: '2000+', desc: 'Homes Served' },
                  { icon: 'ri-24-hours-line', title: '24/7', desc: 'Emergency Service' },
                ].map((stat, index) => (
                  <div key={index} className="bg-white/10 rounded-xl p-5">
                    <i className={`${stat.icon} text-[#FF6B35] text-3xl mb-2 block`}></i>
                    <div className="text-white font-bold text-xl">{stat.title}</div>
                    <div className="text-white/60 text-sm">{stat.desc}</div>
                  </div>
                ))}
              </div>
              {/* Certifications */}
              <div className="mt-8">
                <h3 className="text-white font-semibold mb-4">Our Certifications</h3>
                <div className="flex flex-wrap gap-3">
                  {[
                    'NV Licensed Electrician',
                    'OSHA Certified',
                    'NFPA Member',
                    'Tesla Certified Installer',
                    'Generac Authorized Dealer'
                  ].map((cert, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                      <i className="ri-award-fill text-[#FF6B35]"></i>
                      <span className="text-white text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20electrician%20in%20uniform%20with%20safety%20gear%20working%20on%20residential%20electrical%20installation%20Las%20Vegas%2C%20using%20multimeter%20testing%20equipment%2C%20focused%20expression%2C%20clean%20modern%20home%20interior%20background%2C%20high%20quality%20portrait%20photography&width=600&height=700&seq=electrical-tech-001&orientation=portrait"
                alt="Professional Electrician Las Vegas"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Request Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Get Started</span>
              <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-6">Request Electrical Service</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Fill out the form and one of our electrical experts will contact you within 24 hours to discuss your project and schedule a free estimate.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                    <i className="ri-phone-fill text-[#FF6B35] text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Call Us Directly</p>
                    <a href="tel:7027606213" className="text-xl font-bold text-[#2C2C2C] hover:text-[#FF6B35] cursor-pointer">702-760-6213</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                    <i className="ri-mail-fill text-[#FF6B35] text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Email Us</p>
                    <a href="mailto:info@rwconstructionlv.com" className="text-xl font-bold text-[#2C2C2C] hover:text-[#FF6B35] cursor-pointer">info@rwconstructionlv.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                    <i className="ri-time-fill text-[#FF6B35] text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Business Hours</p>
                    <p className="text-xl font-bold text-[#2C2C2C]">Mon-Sat: 7AM - 6PM</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-[#F8F6F3] rounded-2xl p-8">
              {submitSuccess ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-green-500 text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-[#2C2C2C] mb-3">Request Submitted!</h3>
                  <p className="text-gray-600">We&apos;ll contact you within 24 hours to discuss your electrical project.</p>
                </div>
              ) : (
                <form id="electrical-service-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm"
                        placeholder="Smith"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm"
                      placeholder="(702) 555-0123"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Service Needed *</label>
                    <select
                      name="service_type"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="Panel Upgrade">Panel Upgrade</option>
                      <option value="Wiring/Rewiring">Wiring/Rewiring</option>
                      <option value="Lighting Installation">Lighting Installation</option>
                      <option value="Outlet/Switch Installation">Outlet/Switch Installation</option>
                      <option value="Solar Panel Repair">Solar Panel Repair</option>
                      <option value="EV Charger Installation">EV Charger Installation</option>
                      <option value="Generator Installation">Generator Installation</option>
                      <option value="Commercial Electrical">Commercial Electrical</option>
                      <option value="Electrical Repair">Electrical Repair</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Details</label>
                    <textarea
                      name="project_details"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all text-sm resize-none"
                      placeholder="Please describe your electrical project or issue..."
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#FF6B35] text-white py-4 rounded-lg font-semibold hover:bg-[#E55A2B] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Free Estimate'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">FAQ</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Electrical Service Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'How do I know if I need a panel upgrade?', a: 'Signs you need a panel upgrade include frequently tripped breakers, flickering lights, burning smell near the panel, or if you have an older 100-amp panel and are adding major appliances, EV chargers, or a pool. We offer free inspections to assess your needs.' },
              { q: 'Do you pull permits for electrical work?', a: 'Yes, we pull all required permits and schedule city inspections for electrical work. This ensures your installation is code-compliant and protects your home insurance coverage. Permit costs are included in our estimates.' },
              { q: 'How long does a panel upgrade take?', a: 'A standard panel upgrade typically takes 4-8 hours to complete. This includes removing the old panel, installing the new one, reconnecting all circuits, and testing. We coordinate with the power company for the brief power shutoff.' },
              { q: 'Can you repair my solar panel system?', a: 'Yes! We diagnose and repair all types of solar panel issues including inverter failures, wiring problems, panel damage, and performance issues. We work on systems from all major manufacturers and can help restore your energy production.' },
              { q: 'Do you offer emergency electrical services?', a: 'Yes, we provide 24/7 emergency electrical services for urgent issues like power outages, sparking outlets, burning smells, or exposed wiring. Call our emergency line anytime for immediate assistance.' },
              { q: 'What brands of EV chargers do you install?', a: 'We install all major EV charger brands including Tesla Wall Connector, ChargePoint, JuiceBox, Grizzl-E, and more. We\'ll help you choose the right charger for your vehicle and electrical capacity.' },
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6">
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-red-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Upgrade Your Electrical System?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Get a free electrical inspection and estimate from Las Vegas&apos;s trusted electrical contractors. Licensed, insured, and ready to serve you.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-white text-[#FF6B35] px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Schedule Free Inspection
            </button>
            <a
              href="tel:7027606213"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-760-6213 Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
